import { useSvgToRaster } from "./SvgToRasterContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { ImageDown, Maximize2, Upload } from "lucide-react";

export function SvgToRasterControls() {
    const {
        svg,
        width,
        height,
        transparency,
        antiAliasing,
        maxColors,
        binaryAlpha,
        alphaThreshold,
        autoGenerate,
        error,
        setWidth,
        setHeight,
        setTransparency,
        setAntiAliasing,
        setMaxColors,
        setBinaryAlpha,
        setAlphaThreshold,
        setAutoGenerate,
        generateRasterImage,
        svgInputRef,
        handleFileLoad,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useSvgToRaster();

    const handleWidthChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0 || numValue > 4096)
            return;
        setWidth(numValue);
    };

    const handleHeightChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0 || numValue > 4096)
            return;
        setHeight(numValue);
    };

    const handleMaxColorsChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0 || numValue > 256)
            return;
        setMaxColors(numValue);
    };

    const handleAlphaThresholdChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0 || numValue > 255)
            return;
        setAlphaThreshold(numValue);
    };

    return (
        <div className="panel-controls svg-to-raster-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>SVG to Raster</h2>
                    <ToolDescriptionPopover dialogLabel="SVG to Raster tool description">
                        <p>
                            Convert SVG graphics to raster images (pixel art).
                            Configure output dimensions and transparency
                            settings.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>
            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="svg-to-raster"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <CollapsibleToolGroup title="Source" icon={Upload}>
                    <label className="field-label">SVG File</label>
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => svgInputRef.current?.click()}
                        >
                            Choose SVG file...
                        </button>
                        <input
                            ref={svgInputRef}
                            type="file"
                            accept=".svg,image/svg+xml"
                            className="upload-input"
                            onChange={handleFileLoad}
                        />
                    </div>
                    {svg && (
                        <p className="upload-info">
                            Loaded: <strong>{svg.name}</strong>
                        </p>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Output size" icon={Maximize2}>
                    <div className="field-row">
                        <label className="field-label">
                            Width (px)
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={4096}
                                step={1}
                                value={width}
                                onChange={handleWidthChange}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Height (px)
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={4096}
                                step={1}
                                value={height}
                                onChange={handleHeightChange}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Raster options" icon={ImageDown}>
                    <div className="field-row">
                        <CheckboxField
                            checked={transparency}
                            onChange={setTransparency}
                            label="Enable transparency"
                            inline
                        />
                    </div>

                    <div className="field-row">
                        <CheckboxField
                            checked={antiAliasing}
                            onChange={setAntiAliasing}
                            label="Enable anti-aliasing"
                            inline
                        />
                    </div>

                    <div className="field-row">
                        <CheckboxField
                            checked={binaryAlpha}
                            onChange={setBinaryAlpha}
                            label="Binary alpha (0% or 100% only)"
                            inline
                        />
                    </div>

                    {binaryAlpha && (
                        <div className="field-row">
                            <label className="field-label">
                                Alpha threshold ({alphaThreshold})
                                <input
                                    type="range"
                                    min={0}
                                    max={255}
                                    step={1}
                                    value={alphaThreshold}
                                    className="input-range-full"
                                    onChange={(e) =>
                                        handleAlphaThresholdChange(
                                            Number(e.target.value),
                                        )
                                    }
                                />
                            </label>
                        </div>
                    )}

                    <div className="field-row">
                        <label className="field-label">
                            Max colors (0 = unlimited)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={256}
                                step={1}
                                value={maxColors}
                                onChange={handleMaxColorsChange}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row field-row--inline-actions">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={generateRasterImage}
                            disabled={!svg || width <= 0 || height <= 0}
                        >
                            Generate Raster Image
                        </button>
                        <CheckboxField
                            checked={autoGenerate}
                            onChange={setAutoGenerate}
                            label="Auto-generate"
                            inline
                            className="svg-to-raster-auto-label"
                        />
                    </div>
                </CollapsibleToolGroup>
            </div>

            {error && <p className="upload-error">{error}</p>}
        </div>
    );
}
